import { useEffect, useState } from 'react';

import { Loader2 } from 'lucide-react';

import { cn } from 'lib/utils';
import { useThemeContext } from 'providers/ThemeProvider';

import type { SpinProps, SpinSize, SpinVariant } from './types';

/**
 * Variant styles for different display modes
 */
const variantStyles: Record<SpinVariant, string> = {
  default: '',
  overlay: 'fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center',
  modal: 'fixed inset-0 z-50 bg-black/50 flex items-center justify-center',
  fullscreen:
    'fixed inset-0 z-[9999] bg-background/80 backdrop-blur-sm flex items-center justify-center',
};

/**
 * Spin Component
 *
 * Modern replacement for legacy Spin component using lucide-react + Tailwind CSS.
 * Maintains 100% API compatibility with the original MUI CircularProgress implementation.
 *
 * @component
 * @example
 * ```tsx
 * // Basic usage
 * <Spin />
 *
 * // With loading text
 * <Spin tip="Loading data..." />
 *
 * // Fullscreen overlay (deprecated, use variant)
 * <Spin fullscreen tip="Processing..." />
 *
 * // Modal variant (replaces ModalLoadingFallback)
 * <Spin spinning variant="modal" tip="Carregando..." />
 *
 * // Overlay variant
 * <Spin spinning variant="overlay" tip="Processing..." />
 *
 * // Custom size and delay
 * <Spin size="large" delay={300} />
 *
 * // Custom indicator
 * <Spin indicator={<MyCustomSpinner />} />
 * ```
 */
export const Spin = ({
  spinning = true,
  size = 'default',
  tip,
  delay,
  fullscreen = false,
  variant,
  indicator,
  centerHorizontally = false,
  centerVertically = false,
  className,
  ...rest
}: SpinProps) => {
  const theme = useThemeContext();

  // Implement delay feature (was missing in original component)
  const [delayedSpinning, setDelayedSpinning] = useState(delay ? false : spinning);

  useEffect(() => {
    if (!delay) {
      setDelayedSpinning(spinning);
      return;
    }

    if (!spinning) {
      setDelayedSpinning(false);
      return;
    }

    const timeout = setTimeout(() => {
      setDelayedSpinning(spinning);
    }, delay);

    return () => clearTimeout(timeout);
  }, [spinning, delay]);

  // Don't render anything if not spinning (after delay)
  if (!delayedSpinning) {
    return null;
  }

  // Determine the effective variant (backward compatibility for fullscreen prop)
  const effectiveVariant: SpinVariant = variant || (fullscreen ? 'fullscreen' : 'default');

  // Size mapping: small (20px), default (32px), large (40px)
  const getSizeClasses = (size: SpinSize): string => {
    switch (size) {
      case 'small':
        return 'w-5 h-5'; // 20px
      case 'large':
        return 'w-10 h-10'; // 40px
      default:
        return 'w-8 h-8'; // 32px
    }
  };

  // Get theme color for spinner
  const spinnerColor = theme?.primary || 'hsl(var(--primary))';

  // Default Loader2 spinner
  const defaultIndicator = (
    <Loader2 className={cn('animate-spin', getSizeClasses(size))} style={{ color: spinnerColor }} />
  );

  // Use custom indicator if provided
  const spinnerElement = indicator || defaultIndicator;

  // Wrapper class names for positioning (for default variant)
  const wrapperClasses = cn(
    'inline-block', // Default display
    centerHorizontally && 'mx-auto', // Center horizontally
    centerVertically && 'flex items-center justify-center h-full', // Center vertically
    className
  );

  // Spinner content with optional tip
  const spinnerContent = (
    <div className="flex flex-col items-center gap-2">
      {spinnerElement}
      {tip && (
        <p
          className="text-xs text-primary/80 whitespace-nowrap overflow-hidden text-ellipsis max-w-full"
          data-tip={tip}
        >
          {tip}
        </p>
      )}
    </div>
  );

  // Modal variant: wrap in card
  if (effectiveVariant === 'modal') {
    return (
      <div className={variantStyles.modal}>
        <div className="bg-card rounded-lg p-6 shadow-xl flex flex-col items-center gap-4">
          {spinnerElement}
          {tip && <span className="text-sm text-muted-foreground">{tip}</span>}
        </div>
      </div>
    );
  }

  // Overlay and fullscreen variants
  if (effectiveVariant === 'overlay' || effectiveVariant === 'fullscreen') {
    return (
      <div className={variantStyles[effectiveVariant]}>
        <div className={wrapperClasses} {...rest}>
          {spinnerContent}
        </div>
      </div>
    );
  }

  // Default variant: inline spinner
  return (
    <div className={wrapperClasses} {...rest}>
      {spinnerContent}
    </div>
  );
};

Spin.displayName = 'Spin';

// Re-export types for convenient importing
export type { SpinProps, SpinSize, SpinVariant } from './types';

export default Spin;
